/**
 * PillarPlot — the panel that expands from a pillar header: mode controls, legend, chart.
 *
 * The legend is the honest half of this component. Every metric in the pillar appears in
 * it, including the ones that are NOT on the plot, each carrying the reason it is off —
 * "in %" when the absolute view is showing another unit, "no target set" when nothing can
 * be indexed. Silently dropping them is what would make a grouped plot lie: the Profit
 * story is Revenue against forecast against firm, and two of those three have no targets
 * at all, so in % of target a naive implementation would draw one line and imply the
 * comparison had been made.
 *
 * Controls follow the same rule — the unit switcher only appears where there is more than
 * one unit to switch between.
 */

import React from 'react'
import clsx from 'clsx'
import type { Cadence, MetricFormat, RegistryMetric, ResolveResponse, Site } from '../../types/metrics'
import PillarChart from './PillarChart'
import type { PlotAlign } from './plotAlign'
import {
  buildPillarPlot,
  defaultMode,
  pillarUnits,
  unitLabel,
  PILLAR_SERIES_HEX,
  type PillarPlotModel,
  type PlotMode,
} from './pillarSeries'
import { useTheme } from '../../contexts/ThemeContext'

const TOGGLE = 'rounded px-2 py-0.5 font-mono text-[10px] transition-colors'
const ON = 'bg-panel text-ink shadow-sm'
const OFF = 'text-mut2 hover:text-mut'

function Toggle<T extends string>({
  options,
  value,
  onChange,
  label,
}: {
  options: Array<{ id: T; label: string }>
  value: T
  onChange: (id: T) => void
  label: string
}): React.JSX.Element {
  return (
    <div role="group" aria-label={label} className="flex items-center gap-0.5 rounded-md bg-panel2 p-0.5">
      {options.map((option) => (
        <button
          key={option.id}
          type="button"
          aria-pressed={value === option.id}
          onClick={() => onChange(option.id)}
          className={clsx(TOGGLE, value === option.id ? ON : OFF)}
        >
          {option.label}
        </button>
      ))}
    </div>
  )
}

/** Mode, and — only where there is a choice to make — the unit being plotted. */
function Controls({
  mode,
  onMode,
  units,
  unit,
  onUnit,
}: {
  mode: PlotMode
  onMode: (mode: PlotMode) => void
  units: MetricFormat[]
  unit: MetricFormat | null
  onUnit: (unit: MetricFormat) => void
}): React.JSX.Element {
  return (
    <>
      <Toggle
        label="Plot mode"
        value={mode}
        onChange={onMode}
        options={[
          { id: 'absolute' as PlotMode, label: 'Absolute' },
          { id: 'pct_of_target' as PlotMode, label: '% of target' },
        ]}
      />
      {mode === 'absolute' && units.length > 1 && (
        <Toggle
          label="Unit"
          value={unit ?? units[0]}
          onChange={onUnit}
          options={units.map((u) => ({ id: u, label: unitLabel(u) }))}
        />
      )}
    </>
  )
}

/** Every metric in the pillar, plotted or not — an excluded one keeps its slot colour and
 *  carries the reason, because a series that silently vanishes is how a grouped plot lies. */
function Legend({
  series,
  palette,
}: {
  series: PillarPlotModel['series']
  palette: readonly string[]
}): React.JSX.Element {
  return (
    <ul className="ml-auto flex flex-wrap items-center gap-x-3 gap-y-1">
      {series.map((entry) => (
        <li
          key={entry.metricId}
          className={clsx('flex items-center gap-1.5 font-mono text-[10px]', entry.plotted ? 'text-mut' : 'text-mut2')}
        >
          <span
            aria-hidden
            className={clsx('inline-block h-2 w-2 rounded-full', !entry.plotted && 'opacity-30')}
            style={{ background: palette[entry.colorIndex % palette.length] }}
          />
          <span className={clsx(!entry.plotted && 'line-through decoration-mut2/60')}>{entry.title}</span>
          {entry.excluded && <span className="text-mut2">({entry.excluded})</span>}
        </li>
      ))}
    </ul>
  )
}

export default function PillarPlot({
  metrics,
  resolve,
  site,
  cadence,
  align,
}: {
  metrics: RegistryMetric[]
  resolve: ResolveResponse
  site: Site
  cadence: Cadence
  align?: PlotAlign | null
}): React.JSX.Element {
  const { theme } = useTheme()
  const units = pillarUnits(metrics)
  const [mode, setMode] = React.useState<PlotMode>(() => defaultMode(metrics))
  const [unit, setUnit] = React.useState(() => units[0] ?? null)
  const model = buildPillarPlot(resolve, metrics, site, mode, unit)
  const palette = PILLAR_SERIES_HEX[theme === 'light' ? 'light' : 'dark']

  return (
    // No horizontal padding on the panel: the chart is measured against the table's own
    // columns, so an inset here would push it off them. The controls carry their own.
    <div data-testid="pillar-plot" className="border-b border-line2 bg-bg2/40 py-3" key={cadence}>
      <div className="mb-2 flex flex-wrap items-center gap-2 px-3">
        <Controls mode={mode} onMode={setMode} units={units} unit={unit} onUnit={setUnit} />
        <Legend series={model.series} palette={palette} />
      </div>
      {model.plottedCount > 0 ? (
        <PillarChart model={model} align={align} />
      ) : (
        <p className="py-8 text-center text-xs text-mut2">
          Nothing to plot in this view — every metric in this pillar is excluded above, with the
          reason. Try the other mode.
        </p>
      )}
    </div>
  )
}
